"use client";

import React from "react";
import Image from "next/image";
import { Button, Section, Typography } from "@/components/ui";
import { ButtonConfig } from "../ui/HeroSection";

export type OperationalOutcome = {
  icon: string;
  iconAlt: string;
  title: string;
  description: string;
};

interface OperationalOutcomesSectionProps {
  /** Section title */
  title?: string;
  /** Section subtitle */
  subtitle?: string;
  /** Main image for the left side */
  image: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
  /** The four outcome cards */
  outcomes: OperationalOutcome[];
  /** Optional section background */
  className?: string;
  buttons?: ButtonConfig[];
}

export function OperationalOutcomesSection({
  title = "Operational Outcomes",
  subtitle = "Mission-critical alerts delivered via multiple channels with automatic failover.",
  image,
  outcomes,
  className = "",
  buttons,
}: OperationalOutcomesSectionProps) {
  return (
    <Section variant="default" className={className || undefined}>
      <div className="container mx-auto max-w-6xl px-4 sm:px-6">
        {/* Centered Title and Subtitle */}
        <div className="text-center mb-10 md:mb-14">
          <Typography variant="h2" as="h2" className="mb-4">
            {title}
          </Typography>
          <Typography
            variant="body"
            as="p"
            className="text-text-secondary max-w-2xl mx-auto"
          >
            {subtitle}
          </Typography>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[45%_1fr] gap-8 lg:gap-12 xl:gap-16 items-center">
          {/* Left - Visual */}
          <div className="order-2 lg:order-1">
            <div className="rounded-2xl overflow-hidden w-full">
              <Image
                src={image.src}
                alt={image.alt}
                title={image.alt}
                width={image.width || 560}
                height={image.height || 680}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Right - Outcome Cards (2x2 grid) */}
          <div className="order-1 lg:order-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {outcomes.map((item, idx) => (
                <div
                  key={idx}
                  className="card-default p-4 bg-white border border-[#E2E6EC] flex flex-col justify-between rounded-md shadow-none items-center md:items-start"
                >
                  {/* Icon */}
                  <div className="mb-4">
                    <Image
                      src={item.icon}
                      alt={item.iconAlt}
                      title={item.iconAlt}
                      width={48}
                      height={48}
                    />
                  </div>
                  {/* Title */}
                  <Typography
                    variant="h4"
                    as="h3"
                    className="text-lg font-bold text-text-primary mb-2"
                  >
                    {item.title}
                  </Typography>
                  {/* Description */}
                  <Typography
                    variant="body"
                    as="p"
                    className="text-text-secondary text-sm leading-relaxed"
                  >
                    {item.description}
                  </Typography>

                </div>
              ))}
    {buttons && buttons.length > 0 && (
          <div className=" flex-wrap lg:block hidden gap-4 mt-8">
            {buttons.map((button, index) => (
              <a
                key={index}
                href={button.href || "/book-a-demo"}
                target={button.target || "_self"}
                className={button.variant === "primary" ? "btn-dark btn-md" : "btn-outline-primary btn-md"}
              >
                {button.text}
              </a>
            ))}
          </div>
        )}
            </div>
          </div>
        </div>
            {buttons && buttons.length > 0 && (
          <div className="flex flex-wrap justify-center md:hidden gap-4 mt-8">
            {buttons.map((button, index) => (
              <a
                key={index}
                href={button.href || "/book-a-demo"}
                target={button.target || "_self"}
                className={button.variant === "primary" ? "btn-dark btn-md" : "btn-outline-primary btn-md"}
              >
                {button.text}
              </a>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
